/**
 * Salary card modal for a worker.
 * Shows the current active card (GET /workers/{id}/salary?asOf=today) and lets you set a
 * new one (POST /workers/{id}/salary). Salary is EFFECTIVE-DATED — setting a new card with an
 * effective_from date supersedes the old one; you don't edit a salary in place.
 *
 * Read/write asymmetry: the card reads monthly_salary as a STRING; the body sends a NUMBER.
 */
import React, { useEffect, useState } from 'react';
import { useWorkerMutations } from '../hooks/useWorkerMutations';
import { useWorkerSalary } from '../hooks/useWorkerSalary';
import type { Worker } from '../../../services/manpowerWorkersApi';

interface SalaryModalProps {
	open: boolean;
	worker: Worker | null;
	onClose: () => void;
	onSuccess: (message: string) => void;
}

// LOCAL date as YYYY-MM-DD. Must NOT use toISOString() — that converts to UTC and,
// in a +offset timezone (IST), rolls "today" back to yesterday.
const today = () => {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Format a decimal-STRING money field from the API (e.g. "15000.00") as ₹15,000.
 * Coerces with Number() and guards against undefined/NaN so a malformed value
 * never renders as "NaN" in the current-card header.
 */
const formatMoney = (value?: string | number | null): string => {
	const n = Number(value);
	return Number.isFinite(n) ? `₹${n.toLocaleString('en-IN')}` : '₹0';
};

/** True only for a real, calendar-valid ISO date (YYYY-MM-DD) — rejects e.g. 2026-13-45.
 * Validates against LOCAL date parts; using toISOString() here rolls the date back a day
 * in +offset timezones (IST), which wrongly rejected today and disabled Save. */
const isValidIsoDate = (value: string): boolean => {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
	const [y, m, d] = value.split('-').map(Number);
	const dt = new Date(y, m - 1, d);
	return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

export const SalaryModal: React.FC<SalaryModalProps> = ({ open, worker, onClose, onSuccess }) => {
	const { setSalary } = useWorkerMutations();
	const { data: current, isLoading } = useWorkerSalary(worker?.id);

	const [monthly, setMonthly] = useState('');
	const [payableDays, setPayableDays] = useState('26');
	const [otRate, setOtRate] = useState('0');
	const [effectiveFrom, setEffectiveFrom] = useState(today());
	const [error, setError] = useState<string | null>(null);

	// Seed the form from the worker's CURRENT active salary card so editing is "change a
	// value, keep the rest" rather than blank re-entry. Re-runs once `current` loads (it's
	// async) and whenever the opened worker changes — so values never leak between workers.
	// Amounts come from the current card; effective_from always defaults to TODAY (a new
	// effective-dated card starts now, not back-dated to the old card's date).
	useEffect(() => {
		if (!open) return;
		setMonthly(current?.monthly_salary != null ? String(Number(current.monthly_salary)) : '');
		setPayableDays(current?.payable_days != null ? String(current.payable_days) : '26');
		setOtRate(current?.ot_rate_per_hour != null ? String(Number(current.ot_rate_per_hour)) : '0');
		setEffectiveFrom(today());
		setError(null);
	}, [open, worker?.id, current]);

	if (!open || !worker) return null;

	const monthlyNum = Number(monthly);
	// Worker.id is optional in the schema; a card can't be saved without it.
	const workerId = worker.id;
	const valid =
		typeof workerId === 'number' &&
		monthly !== '' &&
		monthlyNum > 0 &&
		isValidIsoDate(effectiveFrom);

	const handleSubmit = async () => {
		setError(null);
		if (typeof workerId !== 'number') {
			setError('This worker has no id; cannot save a salary card.');
			return;
		}
		try {
			await setSalary.mutateAsync({
				workerId,
				body: {
					monthly_salary: monthlyNum,
					payable_days: Number(payableDays) || 26,
					ot_rate_per_hour: Number(otRate) || 0,
					effective_from: effectiveFrom,
				},
			});
			onSuccess('Salary card saved');
			onClose();
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Something went wrong');
		}
	};

	const inputCls =
		'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none';

	return (
		<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
			<div className='w-full max-w-md rounded-lg bg-white shadow-xl'>
				<div className='border-b border-gray-200 px-5 py-4'>
					<h3 className='text-base font-semibold text-gray-900'>
						Salary — {worker.name ?? `Worker #${worker.id}`}
					</h3>
				</div>

				<div className='space-y-4 px-5 py-4'>
					{/* Current active card */}
					<div className='rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-700'>
						<div className='mb-1 font-medium text-gray-900'>Current (as of today)</div>
						{isLoading ? (
							'Loading…'
						) : current ? (
							<div className='flex flex-wrap gap-x-4 gap-y-1'>
								{/* monthly_salary / ot_rate_per_hour are decimal STRINGS from the API */}
								<span>Monthly: {formatMoney(current.monthly_salary)}</span>
								<span>Payable days: {current.payable_days ?? '—'}</span>
								<span>OT/hr: {formatMoney(current.ot_rate_per_hour)}</span>
								<span>From: {current.effective_from ?? '—'}</span>
							</div>
						) : (
							<span className='text-gray-500'>No salary configured for this worker.</span>
						)}
					</div>

					<div className='text-xs font-semibold text-gray-900'>Set new salary card</div>

					<div className='flex gap-3'>
						<div className='flex-1'>
							<label className='mb-1 block text-xs font-medium text-gray-700'>
								Monthly salary (₹) <span className='text-red-500'>*</span>
							</label>
							<input
								type='number'
								min={1}
								value={monthly}
								onChange={e => setMonthly(e.target.value)}
								placeholder='e.g. 19000'
								className={inputCls}
							/>
						</div>
						<div className='w-28'>
							<label className='mb-1 block text-xs font-medium text-gray-700'>Payable days</label>
							<input type='number' min={1} max={31} value={payableDays} onChange={e => setPayableDays(e.target.value)} className={inputCls} />
						</div>
					</div>

					<div className='flex gap-3'>
						<div className='flex-1'>
							<label className='mb-1 block text-xs font-medium text-gray-700'>OT rate / hour (₹)</label>
							<input type='number' min={0} value={otRate} onChange={e => setOtRate(e.target.value)} className={inputCls} />
						</div>
						<div className='flex-1'>
							<label className='mb-1 block text-xs font-medium text-gray-700'>
								Effective from <span className='text-red-500'>*</span>
							</label>
							<input type='date' value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)} className={inputCls} />
						</div>
					</div>

					{error && <p className='text-[11px] text-red-600'>{error}</p>}
				</div>

				<div className='flex justify-end gap-2 border-t border-gray-200 px-5 py-3'>
					<button
						type='button'
						onClick={onClose}
						disabled={setSalary.isPending}
						className='rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50'
					>
						Cancel
					</button>
					<button
						type='button'
						onClick={handleSubmit}
						disabled={!valid || setSalary.isPending}
						className='rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50'
					>
						{setSalary.isPending ? 'Saving…' : 'Save salary card'}
					</button>
				</div>
			</div>
		</div>
	);
};

export default SalaryModal;
